import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { CheckIcon, InfoIcon } from "@chakra-ui/icons";
import ApiResponse from "../../services/ApiResponse";

interface OpcionDecision {
  id: number;
  texto: string;
  orden: number;
}

interface EscenarioDecision {
  id: number;
  titulo: string;
  descripcion: string;
  orden: number;
  opciones: OpcionDecision[];
}

interface DinamicaDecision {
  id: number;
  titulo: string;
  descripcion: string;
  consigna: string;
  tiempo_limite_segundos: number;
  escenarios: EscenarioDecision[];
}

interface ResultadoDecision {
  perfilPrincipal: {
    id: number;
    codigo: string;
    nombre: string;
    total: string;
  };
  perfilSecundario: {
    id: number;
    codigo: string;
    nombre: string;
    total: string;
  } | null;
  puntajes: any[];
}

interface ResultadoGlobalDecision {
  perfilPrincipal: {
    id: number;
    codigo: string;
    nombre: string;
    total_general: string;
  };
  perfilSecundario: {
    id: number;
    codigo: string;
    nombre: string;
    total_general: string;
  } | null;
  puntajes: any[];
  descripcion: string;
}

const TomaDecisionPage = () => {
  const toast = useToast();

  const [response, setResponse] = useState(new ApiResponse<DinamicaDecision>());
  const [dinamica, setDinamica] = useState<DinamicaDecision | null>(null);
  const [dinamicaId, setDinamicaId] = useState<number>(1);

  const [seleccionadas, setSeleccionadas] = useState<number[]>([]);
  const [intentoId, setIntentoId] = useState<number | null>(null);

  const [resultadoGlobal, setResultadoGlobal] =
    useState<ResultadoGlobalDecision | null>(null);

  const [dinamicasCompletadas, setDinamicasCompletadas] = useState<number[]>([]);
  const [loadingAction, setLoadingAction] = useState(false);

  const usuarioId = 1;

  const escenario = useMemo(() => {
    if (!dinamica?.escenarios?.length) return null;

    return (
      dinamica.escenarios
        .slice()
        .sort((a, b) => a.orden - b.orden)[0] || null
    );
  }, [dinamica]);

  const limiteBase = dinamicaId === 2 ? 4 : 3;

  const limiteSeleccion = escenario?.opciones?.length
    ? Math.min(escenario.opciones.length, limiteBase)
    : limiteBase;

    const glassCard = {
      bg: "rgba(10, 25, 47, 0.6)", // 🔥 oscuro con tono azul (combina con tu fondo)
      border: "1px solid",
      borderColor: "whiteAlpha.200",
      borderRadius: "2xl",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)", // profundidad real
      transition: "all 0.3s ease",
    };
  useEffect(() => {
    const fetchDinamica = async () => {
      const apiResponse = new ApiResponse<DinamicaDecision>();

      await apiResponse.useFetch(`decision/dinamicas/${dinamicaId}`, "GET");

      setResponse(apiResponse);
      setDinamica(apiResponse.data || null);
    };

    fetchDinamica();
  }, [dinamicaId]);

  const limpiarActividadActual = () => {
    setSeleccionadas([]);
    setIntentoId(null);
  };

  const cambiarDinamica = (id: number) => {
    if (resultadoGlobal) return;
    if (dinamicasCompletadas.includes(id)) return;

    setDinamicaId(id);
    limpiarActividadActual();
  };

  const toggleOpcion = (opcionId: number) => {
    if (resultadoGlobal) return;

    setSeleccionadas((prev) => {
      if (prev.includes(opcionId)) {
        return prev.filter((id) => id !== opcionId);
      }

      if (prev.length >= limiteSeleccion) {
        toast({
          title: `Solo podés elegir ${limiteSeleccion} opciones`,
          status: "warning",
          duration: 2500,
          isClosable: true,
        });

        return prev;
      }

      return [...prev, opcionId];
    });
  };

  const iniciarIntento = async () => {
    setLoadingAction(true);

    const apiResponse = new ApiResponse<{ intentoId: number }>();

    await apiResponse.useFetch("decision/intentos", "POST", {
      usuarioId,
      dinamicaId,
    });

    setLoadingAction(false);

    if (apiResponse.responseCode === 200 && apiResponse.data) {
      setIntentoId(apiResponse.data.intentoId);

      toast({
        title: "Dinámica iniciada",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      return;
    }

    toast({
      title: "No se pudo iniciar la dinámica",
      status: "error",
      duration: 2500,
      isClosable: true,
    });
  };

  const obtenerResultadoGlobal = async () => {
    const apiResponse = new ApiResponse<ResultadoGlobalDecision>();

    await apiResponse.useFetch(`decision/resultado-global/${usuarioId}`, "GET");

    if (apiResponse.responseCode === 200 && apiResponse.data) {
      setResultadoGlobal(apiResponse.data);
      return true;
    }

    toast({
      title: "No se pudo obtener el resultado final",
      status: "error",
      duration: 2500,
      isClosable: true,
    });

    return false;
  };

  const finalizarDinamica = async () => {
    if (!intentoId || !escenario) {
      toast({
        title: "Primero iniciá la dinámica",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    if (seleccionadas.length !== limiteSeleccion) {
      toast({
        title: `Debés elegir exactamente ${limiteSeleccion} opciones`,
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setLoadingAction(true);

    const respuestas = seleccionadas.map((opcionId) => ({
      escenarioId: escenario.id,
      opcionId,
    }));

    const guardarResponse = new ApiResponse();

    await guardarResponse.useFetch("decision/respuestas", "POST", {
      intentoId,
      usuarioId,
      dinamicaId,
      respuestas,
    });

    if (guardarResponse.responseCode !== 200) {
      setLoadingAction(false);

      toast({
        title: "Error al guardar respuestas",
        status: "error",
        duration: 2500,
        isClosable: true,
      });

      return;
    }

    const resultadoResponse = new ApiResponse<ResultadoDecision>();

    await resultadoResponse.useFetch("decision/resultado", "POST", {
      intentoId,
      usuarioId,
      dinamicaId,
    });

    if (resultadoResponse.responseCode !== 200 || !resultadoResponse.data) {
      setLoadingAction(false);

      toast({
        title: "Error al calcular resultado de la dinámica",
        status: "error",
        duration: 2500,
        isClosable: true,
      });

      return;
    }

    const nuevasCompletadas = Array.from(
      new Set([...dinamicasCompletadas, dinamicaId])
    );

    setDinamicasCompletadas(nuevasCompletadas);

    const completoIslaYGlobo =
      nuevasCompletadas.includes(1) && nuevasCompletadas.includes(2);

    if (completoIslaYGlobo) {
      limpiarActividadActual();

      await obtenerResultadoGlobal();

      setLoadingAction(false);

      toast({
        title: "Resultado final calculado",
        status: "success",
        duration: 2500,
        isClosable: true,
      });

      return;
    }

    const siguienteDinamica = dinamicaId === 1 ? 2 : 1;

    setDinamicaId(siguienteDinamica);
    limpiarActividadActual();

    setLoadingAction(false);

    toast({
      title: "Dinámica completada",
      description: "Ahora completá la segunda dinámica para ver el resultado final.",
      status: "info",
      duration: 3500,
      isClosable: true,
    });
  };

  const reiniciarProceso = () => {
    setDinamicaId(1);
    setSeleccionadas([]);
    setIntentoId(null);
    setResultadoGlobal(null);
    setDinamicasCompletadas([]);
  };

  if (response.loading) {
    return (
      <Flex justify="center" align="center" h="100vh" bg="blue.900">
        <Spinner size="xl" color="#E9681F" thickness="4px" />
      </Flex>
    );
  }

  return (
    <Box bgGradient="linear(to-b, blue.800, #0a192f)" minH="100vh" pb={20}>
      <Container maxW="container.xl" pt={10}>
        <VStack spacing={3} textAlign="center" mb={10} color="white">
          <Badge bg="#E9681F" color="white" px={4} py={1} borderRadius="full">
            Toma de decisiones
          </Badge>

          <Heading size="2xl" fontWeight="black">
            {resultadoGlobal
              ? "Resultado final"
              : dinamica?.titulo || "Dinámica"}
          </Heading>

          <Text fontSize="xl" color="whiteAlpha.700" maxW="900px">
            {resultadoGlobal
              ? "Completaste las dos dinámicas principales. Este resultado integra tus elecciones en La Isla y el Globo Aerostático."
              : dinamica?.descripcion}
          </Text>
        </VStack>

        {!resultadoGlobal && (
          <>
            <HStack justify="center" mb={4} spacing={4}>
              <Button
                bg={dinamicaId === 1 ? "#E9681F" : "whiteAlpha.200"}
                color="white"
                borderRadius="xl"
                _hover={{ bg: "#c85617" }}
                isDisabled={dinamicasCompletadas.includes(1)}
                onClick={() => cambiarDinamica(1)}
              >
                La Isla
              </Button>

              <Button
                bg={dinamicaId === 2 ? "#E9681F" : "whiteAlpha.200"}
                color="white"
                borderRadius="xl"
                _hover={{ bg: "#c85617" }}
                isDisabled={dinamicasCompletadas.includes(2)}
                onClick={() => cambiarDinamica(2)}
              >
                Globo Aerostático
              </Button>
            </HStack>

            <Flex justify="center" mb={8}>
              <Badge
                bg="whiteAlpha.200"
                color="white"
                px={4}
                py={2}
                borderRadius="full"
              >
                Dinámicas completadas: {dinamicasCompletadas.length}/2
              </Badge>
            </Flex>

            <Box {...glassCard} p={8} mb={8} color="white">
              <Heading size="md" mb={3}>
                Consigna
              </Heading>

              <Text color="whiteAlpha.800" lineHeight="1.8">
                {dinamica?.consigna}
              </Text>
            </Box>

            {!intentoId && (
              <Flex justify="center" mb={8}>
                <Button
                  bg="#E9681F"
                  color="white"
                  size="lg"
                  borderRadius="xl"
                  isLoading={loadingAction}
                  _hover={{ bg: "#c85617" }}
                  onClick={iniciarIntento}
                >
                  Comenzar dinámica
                </Button>
              </Flex>
            )}

            {intentoId && escenario && (
              <Box {...glassCard} p={8} color="white">
                <HStack justify="space-between" mb={5} align="start">
                  <Box>
                    <Heading size="lg">{escenario.titulo}</Heading>

                    <Text color="whiteAlpha.700" mt={2}>
                      {escenario.descripcion}
                    </Text>
                  </Box>

                  <Badge
                    bg="#E9681F"
                    color="white"
                    px={4}
                    py={2}
                    borderRadius="full"
                  >
                    {seleccionadas.length}/{limiteSeleccion} elegidos
                  </Badge>
                </HStack>

                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {escenario.opciones.map((opcion) => {
                    const selected = seleccionadas.includes(opcion.id);

                    return (
                      <Box
                        key={opcion.id}
                        onClick={() => toggleOpcion(opcion.id)}
                        cursor="pointer"
                        p={5}
                        borderRadius="2xl"
                        border="1px solid"
                        borderColor={selected ? "#E9681F" : "whiteAlpha.200"}
                        bg={
                          selected
                            ? "rgba(233, 104, 31, 0.22)"
                            : "rgba(255,255,255,0.04)"
                        }
                        transition="all 0.3s"
                        _hover={{
                          transform: "translateY(-4px)",
                          borderColor: "#E9681F",
                          bg: selected
                            ? "rgba(233, 104, 31, 0.26)"
                            : "rgba(255,255,255,0.09)",
                        }}
                      >
                        <HStack align="start" spacing={3}>
                          {selected && <CheckIcon color="#E9681F" mt={1} />}
                          <Text color="whiteAlpha.900">{opcion.texto}</Text>
                        </HStack>
                      </Box>
                    );
                  })}
                </SimpleGrid>

                <Flex justify="center" mt={8}>
                  <Button
                    bg="#E9681F"
                    color="white"
                    size="lg"
                    borderRadius="xl"
                    isLoading={loadingAction}
                    isDisabled={seleccionadas.length !== limiteSeleccion}
                    _hover={{ bg: "#c85617" }}
                    onClick={finalizarDinamica}
                  >
                    {dinamicasCompletadas.length === 1
                      ? "Finalizar y ver resultado final"
                      : "Finalizar dinámica"}
                  </Button>
                </Flex>
              </Box>
            )}
          </>
        )}

        {resultadoGlobal && (
          <Box {...glassCard} p={8} color="white" mt={8}>
            <Badge
              bg="#E9681F"
              color="white"
              px={4}
              py={1}
              borderRadius="full"
              mb={4}
            >
              Resultado final de toma de decisiones
            </Badge>

            <Heading size="xl" mb={3}>
              Perfil general: {resultadoGlobal.perfilPrincipal.nombre}
            </Heading>

            {resultadoGlobal.perfilSecundario && (
              <Text fontSize="lg" color="whiteAlpha.800" mb={4}>
                Rasgo secundario: {resultadoGlobal.perfilSecundario.nombre}
              </Text>
            )}

            <Text color="whiteAlpha.800" lineHeight="1.8" mb={6}>
              {resultadoGlobal.descripcion}
            </Text>

            <VStack align="stretch" spacing={3}>
              {resultadoGlobal.puntajes.map((p) => (
                <Box key={p.id}>
                  <HStack justify="space-between" mb={1}>
                    <Text>{p.nombre}</Text>
                    <Text color="#E9681F" fontWeight="bold">
                      {p.total_general}
                    </Text>
                  </HStack>

                  <Box h="10px" bg="whiteAlpha.200" borderRadius="full">
                    <Box
                      h="10px"
                      bg="#E9681F"
                      borderRadius="full"
                      w={`${Math.min(Number(p.total_general) * 6, 100)}%`}
                    />
                  </Box>
                </Box>
              ))}
            </VStack>

            <Button
              mt={8}
              leftIcon={<InfoIcon />}
              bg="#E9681F"
              color="white"
              borderRadius="xl"
              _hover={{ bg: "#c85617" }}
              onClick={reiniciarProceso}
            >
              Realizar nuevamente
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default TomaDecisionPage;